import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { message } from 'antd';
import axios from 'axios';
import React from 'react';

import GoogleIcon from '@/components/icons/GoogleIcon';
import { useTranslate } from '@/hooks/useTranslate';
import { checkLogin, loginOrInsert } from '@/services/login';
import useLoginStore from '@/store/Login';
import { setCookie } from '@/utils';
import { GoogleEventID } from '@/utils/eventlog/eventNewConfig';
import { trackGoogleEvent } from '@/utils/eventlog/logAdapter';
import { getFingerprint } from '@/utils/fingerprint';

import styles from './style.module.scss';

interface GoogleLoginProps {
  disabled?: boolean;
  onSuccess: (userInfo: any) => Promise<void>;
  onError?: (error: any) => void;
}

type GOOGLEUSERTYPE = {
  sub: string;
  name: string;
  given_name: string;
  family_name: string;
  picture: string;
  email: string;
  email_verified: boolean;
};

interface GoogleLoginResponse {
  code: number;
  user: GOOGLEUSERTYPE;
  tokens: {
    access_token: string;
    id_token: string;
  };
}

const GoogleLoginButton: React.FC<GoogleLoginProps> = ({
  disabled,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslate();

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: async (response) => {
      try {
        const { code } = response;

        // 发送到后端去交换 token 并登录/注册用户
        const data: GoogleLoginResponse = await axios.post('/api/googleAuth', {
          code,
        });

        if (data.code !== 200) {
          message.error('Google login failed');
          onError?.({ ...data, url: '/api/googleAuth' });
          return;
        }

        const res = await checkLogin({
          accountType: 25,
          accountId: data.user.sub,
        });

        if (res?.data?.state === 1) {
          useLoginStore.getState().setIsNewUser(true);
        }

        const fingerprint = await getFingerprint();
        // 调用 Google 登录接口
        const loginRes = await loginOrInsert({
          accountType: 25,
          accountId: data.user.sub,
          accessToken: data.tokens.access_token,
          byAccessToken: true,
          fingerprint,
        });

        if (loginRes?.code !== 200) {
          message.error('Google login failed');
          onError?.({
            ...loginRes,
            url: '/api/rest/dlt/user/login',
            user: data.user,
          });
          return;
        }

        if (res?.data?.state === 1) {
          // 谷歌登录的新用户
          trackGoogleEvent(GoogleEventID.SignUp, {
            user_id: loginRes.data.uid,
            is_free_user: true,
          });
        }

        // 保存登录信息
        setCookie(
          'user',
          {
            ...loginRes.data,
            accountId: data.user.sub,
            email: data.user.email,
            accountType: 25,
          },
          30,
        );
        if (loginRes.data?.uid) {
          setCookie('falcocut_user_id', loginRes.data.uid, 30);
        }
        // 调用成功回调
        await onSuccess(data.user);
      } catch (error: any) {
        message.error('Google login failed');
        onError?.(error);
      }
    },
    onError: (error) => {
      console.log(error);
      message.error('Google login failed');
      onError?.(error);
    },
    onNonOAuthError: (nonOAuthError) => {
      console.log(nonOAuthError);
      onError?.(nonOAuthError);
    },
  });

  // 处理点击登录按钮
  const handleLoginClick = () => {
    if (disabled) return;
    googleLogin();
  };

  return (
    <button
      className={styles.googleButton}
      onClick={handleLoginClick}
      disabled={disabled}
      type="button"
      element-name="google_btn_login_popup"
    >
      <GoogleIcon />
      <span className=" ml-2 text-[#111111CC] text-[12px]">
        {t('merror.login.google')}
      </span>
    </button>
  );
};

export const GoogleLogin: React.FC<GoogleLoginProps> = (props) => {
  return (
    <GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || ''}>
      <GoogleLoginButton {...props} />
    </GoogleOAuthProvider>
  );
};
